/* src/views/logements/FicheInfosCtrl.jsx — L'ONGLET « Infos » de la fiche
   logement (lot 4, 2026-08-28) : la jauge « Ce qui est réglé », les SEPT
   blocs accordéon (Identité · Prix · Séjour minimum · Taxe de séjour ·
   Caution · Investissement · Facturation propriétaire — le 7e est la
   décision 2 de Dimitri), et les trois renvois « Ce qui se règle ailleurs »
   qui ouvrent des FEUILLES PROVISOIRES (décision 5 : le lot Réglages les
   reprendra ; la matrice espace voyageur, elle, reste ici définitivement).

   ON NE PERD AUCUNE FONCTION : les éditeurs à écriture immédiate DB V2
   (prix de base + application sur plage = CHEMIN ARGENT, min stay + drift,
   plancher, multiplicateur Direct, taxe + crosscheck, caution/Stripe,
   matrice voyageur) sont MONTÉS TELS QUELS — zéro réécriture de leur
   logique. Les champs cfg (identité, forfaits, commission, invest) sont
   recomposés dans l'idiome du proto, mêmes handlers de brouillon.
   Contrat : docs/handoff/INVENTAIRE_LOT4_LOGEMENTS.md §3 Infos. */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '../../supabase'
import { propDisplayName } from '../../lib/format'
import { getBeds24Mapping } from '../../lib/beds24-mapping-lookup'
import { hasValidBareme, resumeBareme } from '../../lib/taxe-commune-helpers'
import { hasCautionAmount } from '../../lib/caution-helpers'
import { resolveCautionFromLog } from '../../lib/caution-amount'
import { getFacturationSource } from '../../lib/facturation-source'
import { getCandidatesForLog, isProviderOrphan } from '../../lib/menage-providers'
import { chargerRegles, poserRegle, retirerRegle } from '../../lib/menage-regles-api'
import BasePriceEditor from '../../components/BasePriceEditor'
import MinStayDefaultEditor from '../../components/MinStayDefaultEditor'
import MinPriceEditor from '../../components/MinPriceEditor'
import ChannelMinStayReadOnly from '../../tabs/logements/ChannelMinStayReadOnly'
import OtaMultipliersReadOnly from '../../tabs/logements/OtaMultipliersReadOnly'
import DirectMultiplierEditor from '../../tabs/logements/DirectMultiplierEditor'
import TaxeReminderPanel from '../../tabs/logements/TaxeReminderPanel'
import { StripeSection } from '../../tabs/logements/InfoFbzTab'
import { JourneyCompositionLogementSection } from '../../components/JourneyCompositionMatrix'
import Feuille from '../accueil/Feuille'
import { JaugeReglage, BlocInfo, LigneKV, LignePleine, ChampProto, Segmentes,
  RenvoiReglage } from './proto/InfosProto'
import { lireAvecCache } from './ficheCache'
// GEL DU TAUX (2026-09-14) — la garde anti-double-facturation existait mais
// n'avait JAMAIS été branchée sur le taux lui-même : cet écran laissait le
// changer sur un mois déjà facturé. Le serveur refuse désormais (api/cfg.js) ;
// ici on le dit AVANT, pour que le refus ne soit pas une surprise au save.
import { findBlockingCommission } from '../../lib/invoice-guard'
import { formatMois } from '../../lib/format'

const STATUTS = [
  { value: 'active', label: 'Actif' },
  { value: 'inactive', label: 'Inactif' },
  { value: 'preparing', label: 'En préparation' },
]

const fmtEuros = (n) => Number(n).toLocaleString('fr-FR', {
  minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'

const noteStyle = { fontSize: '10.5px', color: 'var(--gris)', lineHeight: '1.5',
  margin: '8px 0 0' }
const interStyle = { fontSize: '10px', color: 'var(--gris)', letterSpacing: '.06em',
  textTransform: 'uppercase', fontWeight: '600', margin: '12px 0 6px' }

/* Résumé pricing pour les sous-titres des blocs fermés — un seul GET
   pricing_logement_config (mêmes colonnes que BasePriceEditor). */
function useResumePricing(logementId) {
  const [resume, setResume] = useState(null)
  const recharger = useCallback(async () => {
    try {
      // Cache partagé (appels en série, décision 2026-08-28) — 60 s suffisent
      // pour les sous-titres ; BasePriceEditor garde sa propre lecture fraîche.
      const json = await lireAvecCache(`pricing:${logementId}`, 60000, async () => {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session?.access_token) throw new Error('session absente')
        const qs = new URLSearchParams({
          action: 'pricing_logement_config', logement_id: logementId })
        const res = await fetch(`/api/beds24-mapping?${qs.toString()}`, {
          headers: { Authorization: `Bearer ${session.access_token}` } })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json()
      })
      const row = (json.configs || []).find((r) => r.logement_id === logementId)
      setResume(row || null)
    } catch (e) { console.error('[infos] résumé pricing', e?.message || e) }
  }, [logementId])
  useEffect(() => { recharger() }, [recharger])
  return { resume, recharger }
}

/* Impact du retrait — copie de getDeletionImpact (LogementsPanel, BUG5 :
   factures comptées par logId, jamais par nom). Lot « travail perdu » : un
   logement à chambres porte son historique sous les identifiants de ses
   chambres → on compte le logement ET ses chambres (mêmes identifiants que
   ceux retirés par removeLogConfirmed). */
export function impactSuppression(log, data, invoicesList, incidents) {
  const ids = new Set([log?.id, ...((log?.chambres || []).map((ch) => ch?.id))].filter(Boolean))
  const nbResas = Object.values(data || {})
    .reduce((sum, mk) => sum + [...ids].reduce((s, id) => s + (mk?.[id]?.reservations?.length || 0), 0), 0)
  const nbFactures = (invoicesList || []).filter((f) => ids.has(f.logId)).length
  const nbIncidents = (incidents || []).filter((i) => ids.has(i.logId)).length
  return { nbResas, nbFactures, nbIncidents }
}

export default function FicheInfosCtrl({ draft, data, invoicesList, invoicesRaw, incidents }) {
  const { selected, c } = draft
  const log = selected.entity
  const prop = selected.prop
  const parentLog = selected.parentLog
  const isChambre = selected.isChambre

  const [etatInfo, setEtatInfo] = useState('')
  const [feuille, setFeuille] = useState(null) // 'supprimer'|'proprio'|'beds24'|'voyageur'
  const { resume } = useResumePricing(log.id)

  // Un changement de logement referme les blocs et les feuilles.
  useEffect(() => { setEtatInfo(''); setFeuille(null) }, [log.id])

  // Mapping Beds24 (lecture seule — sous-titre du renvoi).
  const [mapping, setMapping] = useState(null)
  const [mappingError, setMappingError] = useState(null)
  useEffect(() => {
    let vivant = true
    setMapping(null); setMappingError(null)
    getBeds24Mapping(log.id)
      .then((row) => { if (vivant) setMapping(row) })
      .catch(() => { if (vivant) setMappingError('Mapping Beds24 inaccessible. Réessaie ou recharge la page.') })
    return () => { vivant = false }
  }, [log.id])

  // ── Prestataire ménage par défaut (table menage_regles_assignation —
  // écriture IMMÉDIATE, fbz8-cfg jamais écrit pour ce champ, ADR-076). ──
  const [menageUsers, setMenageUsers] = useState([])
  const [menageUsersLoading, setMenageUsersLoading] = useState(true)
  const [menageUsersError, setMenageUsersError] = useState(null)
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const users = await lireAvecCache('admin-users', 300000, async () => {
          const { data: { session } } = await supabase.auth.getSession()
          if (!session?.access_token) throw new Error('Session expirée')
          const res = await fetch('/api/admin-users', {
            headers: { Authorization: `Bearer ${session.access_token}` } })
          if (!res.ok) throw new Error(`HTTP ${res.status}`)
          const j = await res.json()
          return Array.isArray(j.users) ? j.users : []
        })
        if (!cancelled) setMenageUsers(users)
      } catch (e) {
        if (!cancelled) setMenageUsersError(e.message || String(e))
      } finally {
        if (!cancelled) setMenageUsersLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])
  const menageCandidates = getCandidatesForLog(log.id, menageUsers)

  const [regle, setRegle] = useState(null)
  const [regleErreur, setRegleErreur] = useState(null)
  const [regleEnCours, setRegleEnCours] = useState(false)
  const rechargerRegle = useCallback(async () => {
    const { regles } = await chargerRegles(supabase)
    setRegle(regles[log.id] || null)
  }, [log.id])
  useEffect(() => { rechargerRegle() }, [rechargerRegle])
  const menageDefault = regle?.provider_email || ''
  const menageOrphan = isProviderOrphan(menageDefault, menageCandidates)
  const changerRegle = useCallback(async (email) => {
    setRegleEnCours(true); setRegleErreur(null)
    const r = email
      ? await poserRegle(supabase, log.id, email)
      : await retirerRegle(supabase, log.id)
    setRegleEnCours(false)
    if (!r.ok) { setRegleErreur(r.error); return }
    await rechargerRegle()
  }, [log.id, rechargerRegle])

  const updateNested = (parentKey, field, value) => {
    const current = log[parentKey] || {}
    draft.updateLog(parentKey, { ...current, [field]: value })
  }

  // ── Les états « réglé / à compléter » (jauge + points orange) ─────
  const bareme = resumeBareme(log, c)
  const baremeOk = hasValidBareme(log, c)
  const cautionOk = hasCautionAmount(parentLog ? { ...log, parentLog } : log)
  const prefixOk = !!(prop.invoice_prefix || '').trim()
  const invests = log.investissement || []
  const blocs = useMemo(() => ([
    { id: 'identite', ok: !!(log.nom || '').trim() },
    // Inconnu ≠ réglé (décision 2026-08-28 : « un indicateur qui dit complet
    // sur un logement incomplet est pire que pas d'indicateur ») : tant que la
    // config pricing n'est pas lue — ou n'existe pas — le bloc compte comme
    // à compléter.
    { id: 'prix', ok: !!(resume && resume.base_price != null) },
    { id: 'sejour', ok: !!(resume && resume.min_stay_default != null) },
    { id: 'taxe', ok: baremeOk },
    { id: 'caution', ok: cautionOk },
    { id: 'invest', ok: isChambre || invests.length > 0 },
    { id: 'facturation', ok: prefixOk },
  ]), [log.nom, resume, baremeOk, cautionOk, invests.length, prefixOk, isChambre])
  const nbOk = blocs.filter((b) => b.ok).length
  const aCompleter = (id) => !(blocs.find((b) => b.id === id) || { ok: true }).ok

  const basculer = (id) => setEtatInfo((prev) => (prev === id ? '' : id))

  // ── Sous-titres des blocs fermés ──────────────────────────────────
  const sousTitrePrix = resume
    ? (resume.base_price != null
      ? `${fmtEuros(resume.base_price)} la nuit${resume.min_price != null ? ` · plancher ${fmtEuros(resume.min_price)}` : ''}`
      : 'prix de base à poser')
    : 'chargement…'
  const sousTitreSejour = resume
    ? `${resume.min_stay_default != null ? `${resume.min_stay_default} nuit${resume.min_stay_default > 1 ? 's' : ''} minimum` : 'min. séjour à poser'} · ménage ${log.menage_duree_min ?? 90} min`
    : 'chargement…'
  const sousTitreTaxe = bareme
    ? `${bareme.taux_pct} % · plafond ${bareme.plafond_eur.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} € par nuit et par personne`
    : 'barème à valider — carte Adresse des Instructions'
  const plateformes = log.caution_auto_platforms || { direct: true, booking: true }
  const sousTitreCaution = cautionOk
    ? `${fmtEuros(resolveCautionFromLog(parentLog ? { ...log, parentLog } : log))} · ${[plateformes.direct && 'direct', plateformes.booking && 'Booking'].filter(Boolean).join(' et ') || 'aucune plateforme cochée'}`
    : 'montant à définir'
  const totalInvest = invests.reduce((s, i) => s + (Number(i.montant) || 0), 0)
  const sousTitreInvest = isChambre
    ? 'suivi au niveau de la maison'
    : (invests.length ? `${invests.length} poste${invests.length > 1 ? 's' : ''} · ${fmtEuros(totalInvest)}` : 'rien de saisi')
  const modeCommission = log.commissionMode || 'fixed'
  // Le mois COURANT (Paris) est-il déjà facturé pour ce logement ? On lit les
  // lignes BRUTES de `invoices` — la liste mappée ne porte ni `prefix`, ni
  // `invoice_type`, ni `meta.mois`, et `findBlockingCommission` les exige.
  // ⚠️ `invoicesRaw` absent (chargement, ou écran monté sans la prop) ⇒ on
  // n'affirme RIEN : on n'invente pas un gel, et on n'invente pas son absence.
  // Le serveur reste la garde qui tranche.
  const moisCourantLabel = formatMois(
    new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit' })
      .format(new Date()).slice(0, 7))
  const factureDuMois = Array.isArray(invoicesRaw)
    ? findBlockingCommission(invoicesRaw, { logId: log.id, monthLabel: moisCourantLabel })
    : null
  const commissionGelee = !isChambre && factureDuMois != null
  const sousTitreFacturation = isChambre
    ? `préfixe ${prop.invoice_prefix || 'à poser'} · commission au niveau de la maison`
    : `${modeCommission === 'fixed' ? `taux ${log.taux || 0} %` : `dégressif, ${(log.paliers || []).length} palier${(log.paliers || []).length > 1 ? 's' : ''}`} · préfixe ${prop.invoice_prefix || 'à poser'}`

  const nbLogsProp = (prop.logs || []).length

  return (
    <>
      <JaugeReglage n={nbOk} total={blocs.length} />

      {/* ── Identité ─────────────────────────────────────────────── */}
      <BlocInfo id="identite" icone="◈" titre="Identité"
        sousTitre="nom court, nom complet, statut"
        aCompleter={aCompleter('identite')}
        ouvert={etatInfo === ''} onToggle={() => basculer('')}
        enfants={(
          <>
            <LigneKV label="Identifiant">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                <span style={{ fontSize: '10px', color: 'var(--gris-d)' }}>🔒</span>
                <b style={{ fontSize: '12px', color: 'var(--gris-d)' }}>{log.id}</b>
              </span>
            </LigneKV>
            <LignePleine label="Statut">
              <Segmentes options={STATUTS} valeur={log.status || 'active'}
                onChoisir={(v) => draft.updateLog('status', v)}
                testIdPrefix="infos-statut" />
            </LignePleine>
            <LigneKV label="Nom court">
              <ChampProto value={log.nom || ''} testId="infos-nom-court"
                onChange={(v) => draft.updateLog('nom', v)}
                placeholder="Nom court" />
            </LigneKV>
            <LigneKV label="Nom complet" dernier>
              <ChampProto value={log.nom_complet || ''} testId="infos-nom-complet"
                onChange={(v) => draft.updateLog('nom_complet', v)}
                placeholder="comme le nom court" />
            </LigneKV>
            <p className="note">Le nom court apparaît dans les messages au voyageur.
              Le nom complet ne sert que sur les documents.</p>
            {/* Retrait du parc : jamais sur une chambre (volontairement absent —
                inventaire §Liste). Feuille avec impacts chiffrés + RETIRER.
                Libellé validé par Dimitri le 03/10. */}
            {!isChambre && (
              <div style={{ marginTop: '10px', paddingTop: '10px',
                borderTop: '1px solid var(--bord)' }}>
                <button className="bt bt-s" data-testid="infos-supprimer-logement"
                  style={{ color: '#b8412b', borderColor: '#e8c4b8' }}
                  onClick={() => setFeuille('supprimer')}>
                  Retirer ce logement du parc…</button>
              </div>
            )}
          </>
        )} />

      {/* ── Prix ─────────────────────────────────────────────────── */}
      <BlocInfo id="prix" icone="€" titre="Prix"
        sousTitre={sousTitrePrix} aCompleter={aCompleter('prix')}
        ouvert={etatInfo === 'prix'} onToggle={() => basculer('prix')}
        enfants={(
          <>
            {/* CHEMIN ARGENT — éditeurs montés tels quels (zéro réécriture,
                cf. en-tête). Le dessin interne suit leur DS : arbitrage visuel
                à l'écran par Dimitri, la mécanique ne bouge pas. */}
            <BasePriceEditor logementId={log.id} />
            <div style={{ marginTop: '12px', paddingTop: '12px',
              borderTop: '1px solid var(--bord)' }}>
              <MinPriceEditor logementId={log.id} />
            </div>
            <div style={{ marginTop: '12px', paddingTop: '12px',
              borderTop: '1px solid var(--bord)' }}>
              <OtaMultipliersReadOnly logementId={log.id} />
            </div>
            <div style={{ marginTop: '12px', paddingTop: '12px',
              borderTop: '1px solid var(--bord)' }}>
              {isChambre ? (
                <DirectMultiplierEditor logementId={log.id} />
              ) : (
                <p style={noteStyle}>Le multiplicateur Direct n'est réglable que
                  sur les sous-locations vendables en direct (lc1/lc2/lc3).</p>
              )}
            </div>
            <div style={{ marginTop: '12px', paddingTop: '12px',
              borderTop: '1px solid var(--bord)' }}>
              <LigneKV label="Prix ménage — réservations en direct" dernier>
                {/* 🔴 ZONE ROUGE (chemin argent) — pricing.cleaning_fee = prix
                    payé par le voyageur au tunnel direct. DISTINCT de
                    tarifMenage (refacturation proprio). */}
                <ChampProto type="number" testId="infos-cleaning-fee"
                  value={log.pricing?.cleaning_fee ?? ''}
                  onChange={(v) => updateNested('pricing', 'cleaning_fee', parseFloat(v) || 0)}
                  placeholder="ex: 50" suffixe="€" />
              </LigneKV>
              <p style={noteStyle}>Facturé au voyageur sur le site direct. Le
                ménage Airbnb/Booking se gère directement sur les plateformes.</p>
            </div>
          </>
        )} />

      {/* ── Séjour minimum ───────────────────────────────────────── */}
      <BlocInfo id="sejour" icone="◷" titre="Séjour minimum"
        sousTitre={sousTitreSejour} aCompleter={aCompleter('sejour')}
        ouvert={etatInfo === 'sejour'} onToggle={() => basculer('sejour')}
        enfants={(
          <>
            <MinStayDefaultEditor logementId={log.id} mode="section" onSaved={() => {}} />
            <p style={noteStyle}>Écrit le min. séjour <b>par défaut</b> dans
              Beds24 — la valeur du site direct, et le repli des canaux sans
              règle propre. Les min. séjours par canal gardent la main dans Beds24.</p>
            <ChannelMinStayReadOnly logementId={log.id} />
            <div style={{ marginTop: '12px', paddingTop: '12px',
              borderTop: '1px solid var(--bord)' }}>
              <LigneKV label="Durée de ménage minimum (minutes)">
                <ChampProto type="number" testId="infos-menage-duree"
                  value={log.menage_duree_min ?? ''}
                  onChange={(v) => {
                    const n = parseInt(v, 10)
                    draft.updateLog('menage_duree_min',
                      Number.isFinite(n) && n > 0 ? n : undefined)
                  }}
                  placeholder="90 (défaut)" suffixe="min" />
              </LigneKV>
              <LignePleine label="Prestataire ménage assigné par défaut" dernier>
                <select data-testid="regle-menage-select"
                  value={menageDefault}
                  onChange={(e) => changerRegle(e.target.value)}
                  disabled={menageUsersLoading || regleEnCours}
                  style={{ width: '100%', minHeight: '38px', padding: '0 10px',
                    borderRadius: '9px', background: '#f8fafc',
                    border: '1px solid #d5e3ee', fontSize: '12.5px',
                    color: 'var(--navy-d)',
                    cursor: menageUsersLoading || regleEnCours ? 'wait' : 'pointer' }}>
                  <option value="">
                    {menageUsersLoading ? 'Chargement comptes…'
                      : '— Aucun (à assigner manuellement par résa) —'}
                  </option>
                  {menageOrphan && (
                    <option value={menageDefault}>{menageDefault} (visibilité retirée)</option>
                  )}
                  {menageCandidates.map((cand) => (
                    <option key={cand.id || cand.email} value={cand.email}>{cand.email}</option>
                  ))}
                </select>
                {regle && (
                  <p style={noteStyle} data-testid="regle-menage-trace">
                    {(regle.posee_par || '').startsWith('migration:') ||
                    regle.posee_par_role === 'systeme'
                      ? 'Reprise automatique lors de la migration.'
                      : `Posée par ${regle.posee_par || 'inconnu'}${regle.posee_le
                        ? ` le ${regle.posee_le.slice(8, 10)}/${regle.posee_le.slice(5, 7)}/${regle.posee_le.slice(0, 4)}`
                        : ''}`}
                  </p>
                )}
                {regleErreur && (
                  <div className="info" data-testid="regle-menage-erreur"
                    style={{ marginTop: '6px', borderColor: '#e8c4b8',
                      background: '#fbeee9', color: '#8a3b28' }}>{regleErreur}</div>
                )}
                {menageUsersError && (
                  <div className="info" style={{ marginTop: '6px',
                    borderColor: '#e8c4b8', background: '#fbeee9',
                    color: '#8a3b28' }}>Erreur chargement comptes ménage : {menageUsersError}</div>
                )}
                {menageOrphan && (
                  <div className="info" style={{ marginTop: '6px',
                    borderColor: '#e8c9a0', background: '#fdf6ec', color: '#8a5a17' }}>
                    Le compte <b>{menageDefault}</b> n'a plus accès à ce logement
                    (visibilité retirée dans Paramètres &gt; Comptes). La valeur
                    reste enregistrée mais ne sera pas appliquée tant qu'elle
                    n'apparaît pas dans la liste candidats.</div>
                )}
                {!menageUsersLoading && !menageDefault && menageCandidates.length === 0 && (
                  <p style={noteStyle}>Aucun compte ménage n'a accès à ce logement.
                    Configure d'abord l'attribution dans <b>Paramètres &gt; Comptes</b>.</p>
                )}
                {!menageUsersLoading && !menageDefault && menageCandidates.length > 0 && (
                  <p style={noteStyle}>Sans défaut, chaque résa devra être assignée
                    manuellement.</p>
                )}
              </LignePleine>
            </div>
          </>
        )} />

      {/* ── Taxe de séjour ───────────────────────────────────────── */}
      <BlocInfo id="taxe" icone="◔" titre="Taxe de séjour"
        sousTitre={sousTitreTaxe} aCompleter={aCompleter('taxe')}
        ouvert={etatInfo === 'taxe'} onToggle={() => basculer('taxe')}
        enfants={<TaxeReminderPanel log={log} cfg={c} />} />

      {/* ── Caution ──────────────────────────────────────────────── */}
      <BlocInfo id="caution" icone="◫" titre="Caution"
        sousTitre={sousTitreCaution} aCompleter={aCompleter('caution')}
        ouvert={etatInfo === 'caution'} onToggle={() => basculer('caution')}
        enfants={(
          <>
            {/* CHEMIN ARGENT (pré-autorisation Stripe + contrat) — section
                montée telle quelle. ⚠️ Piège documenté : « Sauvegarder » ici
                n'écrit que le brouillon, la pastille Enregistrer reste requise. */}
            <StripeSection log={log} onUpdate={draft.updateLog} parentLog={parentLog} />
            <p style={noteStyle}>Ces réglages s'enregistrent avec la pastille
              « Enregistrer » en haut de la fiche.</p>
          </>
        )} />

      {/* ── Investissement ───────────────────────────────────────── */}
      <BlocInfo id="invest" icone="◑" titre="Investissement"
        sousTitre={sousTitreInvest} aCompleter={aCompleter('invest')}
        ouvert={etatInfo === 'invest'} onToggle={() => basculer('invest')}
        enfants={isChambre ? (
          <p style={noteStyle}>L'investissement initial se suit au niveau de la
            maison, pas de la chambre.</p>
        ) : (
          <InvestissementBloc invests={invests}
            onAdd={draft.addInvest} onRemove={draft.removeInvest} />
        )} />

      {/* ── Facturation propriétaire (6e bloc — décision 2) ──────── */}
      <BlocInfo id="facturation" icone="▤" titre="Facturation propriétaire"
        sousTitre={sousTitreFacturation} aCompleter={aCompleter('facturation')}
        ouvert={etatInfo === 'facturation'} onToggle={() => basculer('facturation')}
        enfants={(
          <>
            {isChambre ? (
              <p style={{ ...noteStyle, margin: '0 0 8px' }}>La commission se
                règle au niveau de la maison — ici ne vivent que le forfait
                ménage et la série de factures.</p>
            ) : (
              <>
                {commissionGelee && (
                  <p data-testid="infos-commission-gelee"
                    style={{ ...noteStyle, margin: '0 0 8px', color: '#8a4a30' }}>
                    ⛔ <b>{moisCourantLabel} est déjà facturé</b> pour ce logement — le taux
                    ne bouge plus. La facture est partie avec l’ancien taux ; le changer
                    ferait dire à cet écran autre chose que la pièce que le propriétaire
                    a reçue. Le serveur refuse aussi cet enregistrement.
                  </p>
                )}
                <LignePleine label="Mode de commission">
                  <Segmentes testIdPrefix="infos-commission"
                    options={[{ value: 'fixed', label: 'Taux fixe' },
                      { value: 'degressive', label: 'Dégressif' }]}
                    valeur={modeCommission} disabled={commissionGelee}
                    onChoisir={(v) => draft.updateLog('commissionMode', v)} />
                </LignePleine>
                {modeCommission === 'fixed' && (
                  <LigneKV label="Taux">
                    <ChampProto type="number" testId="infos-taux"
                      value={log.taux || 0} disabled={commissionGelee}
                      onChange={(v) => draft.updateLog('taux', parseFloat(v) || 0)}
                      suffixe="%" />
                  </LigneKV>
                )}
                {modeCommission === 'degressive' && (
                  <LignePleine label="Paliers (du plus court au plus long)">
                    {(log.paliers || []).map((p, pi) => (
                      <div key={pi} style={{ display: 'flex', gap: '6px',
                        alignItems: 'center', marginBottom: '5px' }}>
                        <span style={{ fontSize: '11px', color: 'var(--gris)' }}>≤</span>
                        <ChampProto type="number" value={p.maxNuits} disabled={commissionGelee}
                          onChange={(v) => draft.updateLogPaliers(pi, 'maxNuits', parseInt(v) || 0)} />
                        <span style={{ fontSize: '11px', color: 'var(--gris)' }}>nuits →</span>
                        <ChampProto type="number" value={p.taux} disabled={commissionGelee}
                          onChange={(v) => draft.updateLogPaliers(pi, 'taux', parseFloat(v) || 0)}
                          suffixe="%" />
                        <button onClick={() => !commissionGelee && draft.removePalier(pi)}
                          disabled={commissionGelee}
                          aria-label="Retirer le palier"
                          style={{ background: 'none', border: 'none', cursor: 'pointer',
                            color: '#b8412b', fontSize: '14px' }}>✕</button>
                      </div>
                    ))}
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center',
                      marginTop: '4px' }}>
                      <span style={{ fontSize: '11px', color: 'var(--gris)' }}>Au-delà :</span>
                      <ChampProto type="number" value={log.tauxBase ?? log.taux ?? 0}
                        disabled={commissionGelee}
                        onChange={(v) => draft.updateLog('tauxBase', parseFloat(v) || 0)}
                        suffixe="%" />
                    </div>
                    <button className="bt bt-s" style={{ marginTop: '8px' }}
                      disabled={commissionGelee}
                      onClick={() => !commissionGelee && draft.addPalier()}>+ Palier</button>
                  </LignePleine>
                )}
              </>
            )}
            <LigneKV label="Forfait ménage refacturé au propriétaire">
              <ChampProto type="number" testId="infos-tarif-menage"
                value={log.tarifMenage || ''}
                onChange={(v) => draft.updateLog('tarifMenage', parseFloat(v) || 0)}
                placeholder="0" suffixe="€" />
            </LigneKV>
            <LigneKV label="Forfait société de ménage (€/intervention, optionnel)">
              <ChampProto type="number" testId="infos-cout-menage"
                value={log.menRealCostPerClean ?? ''}
                onChange={(v) => draft.updateLog('menRealCostPerClean',
                  v === '' ? undefined : parseFloat(v) || 0)}
                placeholder="—" suffixe="€" />
            </LigneKV>
            <LignePleine label="Source de facturation">
              <select value={getFacturationSource(log)}
                data-testid="infos-facturation-source"
                onChange={(e) => draft.updateLog('facturationSource', e.target.value)}
                style={{ width: '100%', minHeight: '38px', padding: '0 10px',
                  borderRadius: '9px', background: '#f8fafc',
                  border: '1px solid #d5e3ee', fontSize: '12.5px',
                  color: 'var(--navy-d)' }}>
                <option value="kv">V1 — imports CSV (défaut)</option>
                <option value="v2">V2 — sync Beds24 (auto)</option>
              </select>
              <p style={noteStyle}>La bascule ne s'applique qu'aux mois non encore
                facturés. Les factures déjà émises restent inchangées.</p>
            </LignePleine>
            <LigneKV label="Préfixe factures (multi-séquences)" dernier>
              <ChampProto testId="infos-invoice-prefix"
                value={prop.invoice_prefix || ''}
                onChange={(v) => draft.updateProp(selected.propIdx, 'invoice_prefix',
                  v.toUpperCase().replace(/[^A-Z0-9-]/g, ''))}
                placeholder="ex: AVE — requis pour émettre" />
            </LigneKV>
            <p style={noteStyle}>Sans préfixe, l'émission des reçus voyageur de ce
              propriétaire est désactivée. Le préfixe vaut pour tous ses logements.</p>
          </>
        )} />

      {/* ── Ce qui se règle ailleurs ─────────────────────────────── */}
      <p className="inter" style={interStyle}>Ce qui se règle ailleurs</p>
      <RenvoiReglage titre="Le propriétaire" testId="infos-renvoi-proprio"
        sousTitre={`${propDisplayName(prop) || prop.nom || '—'} — partagé par ${nbLogsProp} logement${nbLogsProp > 1 ? 's' : ''}`}
        point={!(prop.adresse && prop.code_postal && prop.ville)}
        onClick={() => setFeuille('proprio')} />
      <RenvoiReglage titre="Le raccordement Beds24" testId="infos-renvoi-beds24"
        sousTitre={mapping
          ? `propriété ${mapping.beds24_property_id} · chambre ${mapping.beds24_room_id}`
          : (mappingError || 'pas de mapping configuré')}
        point={!mapping}
        onClick={() => setFeuille('beds24')} />
      <RenvoiReglage titre="L'espace voyageur" testId="infos-renvoi-voyageur"
        sousTitre="ce que reçoit le voyageur, canal par canal"
        onClick={() => setFeuille('voyageur')} />
      <p className="note">Le propriétaire et le raccordement valent pour d'autres
        logements — leurs feuilles rejoindront Réglages. L'espace voyageur, lui,
        est bien une exception de CE logement.</p>

      {/* ── Les feuilles ─────────────────────────────────────────── */}
      {feuille === 'supprimer' && (
        <FeuilleSupprimerLogement
          log={log}
          impact={impactSuppression(log, data, invoicesList, incidents)}
          onConfirmer={() => {
            draft.removeLogConfirmed(selected.propIdx, selected.logIdx)
            setFeuille(null)
          }}
          onFermer={() => setFeuille(null)} />
      )}
      {feuille === 'proprio' && (
        <FeuilleProprio prop={prop} propIdx={selected.propIdx}
          updateProp={draft.updateProp}
          onSupprimer={() => {
            draft.removePropConfirmed(selected.propIdx)
            setFeuille(null)
          }}
          onFermer={() => setFeuille(null)} />
      )}
      {feuille === 'beds24' && (
        <Feuille titre="Le raccordement Beds24" testId="feuille-beds24"
          onFermer={() => setFeuille(null)}
          enfants={(
            <>
              {mapping ? (
                <div className="carte" style={{ marginBottom: '10px' }}>
                  <p style={{ fontSize: '12.5px', fontWeight: '600',
                    color: 'var(--navy-d)', margin: '0 0 4px' }}>
                    Property {mapping.beds24_property_id} · Room {mapping.beds24_room_id}</p>
                  {mapping.property_name && (
                    <p style={{ fontSize: '11px', color: 'var(--gris)',
                      margin: 0 }}>{mapping.property_name}</p>
                  )}
                </div>
              ) : (
                <div className="info" style={{ borderColor: '#e8c9a0',
                  background: '#fdf6ec', color: '#8a5a17' }}>
                  {mappingError || 'Pas de mapping configuré pour ce logement.'}
                </div>
              )}
              <p style={{ fontSize: '11.5px', color: 'var(--gris)',
                lineHeight: '1.55', margin: '8px 0 0' }}>
                Le raccordement se configure dans <b>Paramètres → Beds24</b>
                (écran existant). Provisoire : cette feuille rejoindra
                Réglages quand son lot arrivera.</p>
              <div className="act">
                <button className="bt bt-s" onClick={() => setFeuille(null)}>Fermer</button>
              </div>
            </>
          )} />
      )}
      {feuille === 'voyageur' && (
        <Feuille titre="L'espace voyageur" suffixe={log.id} large
          testId="feuille-espace-voyageur"
          onFermer={() => setFeuille(null)}
          enfants={(
            <>
              {/* La matrice PAR LOGEMENT reste ici définitivement (décision 5) :
                  c'est une exception de ce logement, pas un réglage global. */}
              <JourneyCompositionLogementSection logementId={log.id} />
              <div className="act">
                <button className="bt bt-s" onClick={() => setFeuille(null)}>Fermer</button>
              </div>
            </>
          )} />
      )}
    </>
  )
}

/* ── le bâtisseur d'investissement (idiome proto) ─────────────────── */
function InvestissementBloc({ invests, onAdd, onRemove }) {
  const [label, setLabel] = useState('')
  const [montant, setMontant] = useState('')
  return (
    <>
      {invests.length === 0 && (
        <p style={{ ...noteStyle, margin: '0 0 8px' }}>Aucun investissement initial.</p>
      )}
      {invests.map((inv) => (
        <LigneKV key={inv.id} label={inv.label}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            <b style={{ fontSize: '12px', color: 'var(--navy-d)' }}>{fmtEuros(inv.montant)}</b>
            <button onClick={() => onRemove(inv.id)} aria-label="Retirer"
              style={{ background: 'none', border: 'none', cursor: 'pointer',
                color: '#b8412b', fontSize: '14px' }}>✕</button>
          </span>
        </LigneKV>
      ))}
      <div style={{ display: 'flex', gap: '6px', marginTop: '9px', flexWrap: 'wrap' }}>
        <input value={label} onChange={(e) => setLabel(e.target.value)}
          placeholder="Libellé (ex: mobilier)" data-testid="invest-label"
          style={{ flex: '1', minWidth: '130px', minHeight: '38px', padding: '0 12px',
            borderRadius: '9px', background: '#f8fafc', border: '1px solid #d5e3ee',
            fontSize: '12.5px', color: 'var(--navy-d)', outline: 'none' }} />
        <input type="number" value={montant} onChange={(e) => setMontant(e.target.value)}
          placeholder="€" data-testid="invest-montant"
          style={{ width: '90px', minHeight: '38px', padding: '0 12px',
            borderRadius: '9px', background: '#f8fafc', border: '1px solid #d5e3ee',
            fontSize: '12.5px', color: 'var(--navy-d)', outline: 'none' }} />
        <button className="bt bt-s" data-testid="invest-ajouter"
          onClick={() => {
            if (!label || !montant) return
            onAdd(label, montant)
            setLabel(''); setMontant('')
          }}>+ Ajouter</button>
      </div>
    </>
  )
}

/* ── la feuille « Retirer du parc » (impacts + RETIRER) ─────────────
   Lot « travail perdu » : retirer un logement n'efface plus rien (relevés,
   facturation, incidents restent — consigne Dimitri « ne pas étendre la
   suppression »). Texte validé par Dimitri le 03/10 : il dit ce qui se passe
   vraiment. Les data-testid historiques sont conservés (sélecteurs). */
export function FeuilleSupprimerLogement({ log, impact, onConfirmer, onFermer }) {
  const [tape, setTape] = useState('')
  const ok = tape === 'RETIRER'
  return (
    <Feuille titre={`Retirer « ${log.nom} » du parc ?`} testId="feuille-supprimer-logement"
      onFermer={onFermer}
      enfants={(
        <>
          <p style={{ fontSize: '12px', color: 'var(--navy-d)', lineHeight: '1.55',
            margin: '0 0 10px' }}>
            Le logement et ses chambres disparaîtront de ton parc après 💾.</p>
          <div className="carte" style={{ background: '#f4f7fa',
            borderColor: '#d5e3ee', marginBottom: '10px' }}>
            <p style={{ fontSize: '12px', color: 'var(--navy-d)', margin: '2px 0' }}>
              Rien n'est effacé : ses <b>{impact.nbResas}</b> réservation{impact.nbResas > 1 ? 's' : ''},{' '}
              <b>{impact.nbFactures}</b> facture{impact.nbFactures > 1 ? 's' : ''} et{' '}
              <b>{impact.nbIncidents}</b> incident{impact.nbIncidents > 1 ? 's' : ''} restent
              dans l'historique et la comptabilité.</p>
          </div>
          <p style={{ fontSize: '11.5px', color: 'var(--gris)', margin: '0 0 6px' }}>
            Tape <code style={{ background: '#eef2f6', padding: '2px 6px',
              borderRadius: '4px', fontWeight: '700' }}>RETIRER</code> pour confirmer.</p>
          <input value={tape} onChange={(e) => setTape(e.target.value)} autoFocus
            data-testid="supprimer-confirmation"
            style={{ width: '100%', minHeight: '38px', padding: '0 12px',
              borderRadius: '9px', background: '#f8fafc', border: '1px solid #d5e3ee',
              fontSize: '12.5px', outline: 'none', marginBottom: '12px' }} />
          <div className="act">
            <button className="bt bt-s" onClick={onFermer}>Annuler</button>
            <button className="bt" data-testid="supprimer-definitivement"
              disabled={!ok}
              style={{ background: ok ? '#b8412b' : '#cdd5df', color: '#fff',
                cursor: ok ? 'pointer' : 'not-allowed' }}
              onClick={() => { if (ok) onConfirmer() }}>
              Retirer du parc</button>
          </div>
        </>
      )} />
  )
}

/* ── la feuille provisoire « Le propriétaire » (décision 5) ───────── */
function FeuilleProprio({ prop, propIdx, updateProp, onSupprimer, onFermer }) {
  const personnePhysique = !!(prop.nom_famille || prop.prenom)
  const nbLogs = (prop.logs || []).length
  const [confirmeSuppr, setConfirmeSuppr] = useState(false)
  const champ = (label, field, type = 'text', placeholder = '') => (
    <LignePleine label={label}>
      <input type={type} value={prop[field] || ''}
        data-testid={`proprio-${field}`}
        onChange={(e) => updateProp(propIdx, field, e.target.value)}
        placeholder={placeholder}
        style={{ width: '100%', minHeight: '38px', padding: '0 12px',
          borderRadius: '9px', background: '#f8fafc', border: '1px solid #d5e3ee',
          fontSize: '12.5px', color: 'var(--navy-d)', outline: 'none' }} />
    </LignePleine>
  )
  return (
    <Feuille titre="Le propriétaire" suffixe={propDisplayName(prop) || prop.nom}
      testId="feuille-proprio" onFermer={onFermer}
      enfants={(
        <>
          <div className="info">Ces informations sont partagées par TOUS les
            logements de ce propriétaire ({nbLogs} logement{nbLogs > 1 ? 's' : ''}).
            Toute modification ici se répercute partout.</div>
          {personnePhysique ? (
            <>
              {champ('Nom de famille', 'nom_famille')}
              {champ('Prénom', 'prenom')}
            </>
          ) : (
            champ('Nom', 'nom')
          )}
          {champ('Email', 'email', 'email')}
          {champ('Téléphone', 'telephone', 'tel')}
          {champ('Adresse (facturation propriétaire)', 'adresse', 'text',
            'obligatoire pour les contrats')}
          {champ('Code postal', 'code_postal')}
          {champ('Ville', 'ville')}
          <p style={{ fontSize: '10.5px', color: 'var(--gris)', lineHeight: '1.5',
            margin: '8px 0 0' }}>
            Sans adresse complète, aucun contrat ne peut se générer pour ses
            logements. Ces champs s'enregistrent avec la pastille « Enregistrer ».
            Provisoire : cette feuille rejoindra Réglages · Propriétaires.</p>
          <div style={{ marginTop: '12px', paddingTop: '10px',
            borderTop: '1px solid var(--bord)' }}>
            {nbLogs > 0 ? (
              <p style={{ fontSize: '10.5px', color: 'var(--gris)', margin: 0 }}>
                Ce propriétaire a encore {nbLogs} logement{nbLogs > 1 ? 's' : ''} —
                il ne peut pas être supprimé.</p>
            ) : confirmeSuppr ? (
              <span style={{ display: 'inline-flex', gap: '7px', alignItems: 'center' }}>
                <span style={{ fontSize: '11.5px', color: '#8a3b28' }}>Supprimer ce
                  propriétaire ?</span>
                <button className="bt bt-s" data-testid="proprio-supprimer-oui"
                  style={{ color: '#b8412b', borderColor: '#e8c4b8' }}
                  onClick={onSupprimer}>Oui</button>
                <button className="bt bt-s"
                  onClick={() => setConfirmeSuppr(false)}>Non</button>
              </span>
            ) : (
              <button className="bt bt-s" data-testid="proprio-supprimer"
                style={{ color: '#b8412b', borderColor: '#e8c4b8' }}
                onClick={() => setConfirmeSuppr(true)}>Supprimer ce propriétaire…</button>
            )}
          </div>
          <div className="act">
            <button className="bt bt-s" onClick={onFermer}>Fermer</button>
          </div>
        </>
      )} />
  )
}
